import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import ThemeToggle from '../ui/ThemeToggle';

const Header = ({ onDownloadClick }) => {
  const theme    = useTheme();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [isScrolled,      setIsScrolled]      = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const navItems = [
    { label: 'Features',    href: '#features' },
    { label: 'How It Works', href: '#how-it-works' },
    { label: 'Pricing',     href: '#pricing' },
    { label: 'FAQ',         href: '#faq' },
    { label: 'For Business', href: '#vouchers' },
  ];

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  function scrollToSection(href) {
    setIsMobileMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }

  async function handleLogout() {
    setIsMobileMenuOpen(false);
    await logout();
    navigate('/');
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'py-3' : 'py-5'}`}
      style={{
        backgroundColor: isScrolled
          ? (theme.isDark ? 'rgba(47, 47, 47, 0.95)' : 'rgba(255, 255, 255, 0.95)')
          : 'transparent',
        backdropFilter: isScrolled ? 'blur(20px)' : 'none',
        borderBottom: isScrolled ? `1px solid ${theme.border}` : 'none',
      }}
    >
      <div className="container-app">
        <nav className="flex items-center justify-between">

          {/* Logo */}
          <a
            href="#"
            className="flex items-center group"
            onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          >
            <img src="/logo.png" alt="TripData" className="h-8 object-contain" />
          </a>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map(item => (
              <a
                key={item.label}
                href={item.href}
                onClick={e => { e.preventDefault(); scrollToSection(item.href); }}
                className="text-base font-medium transition-colors hover:opacity-70"
                style={{ color: theme.textSecondary }}
              >
                {item.label}
              </a>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center gap-3">
            <ThemeToggle />

            {user ? (
              // Logged-in state
              <>
                <Link
                  to="/portal"
                  className="hidden md:inline-flex btn btn-gradient text-sm px-5 py-2.5"
                >
                  My Portal
                </Link>
                <button
                  onClick={handleLogout}
                  className="hidden md:inline-flex text-sm font-medium transition-colors hover:opacity-70"
                  style={{ color: theme.textSecondary }}
                >
                  Sign out
                </button>
              </>
            ) : (
              // Guest state
              <>
                <Link
                  to="/login"
                  className="hidden md:inline-flex text-sm font-medium transition-colors hover:opacity-70"
                  style={{ color: theme.textSecondary }}
                >
                  Sign in
                </Link>
                <button
                  onClick={onDownloadClick}
                  className="hidden md:inline-flex btn btn-gradient text-sm px-5 py-2.5"
                >
                  Download App
                </button>
              </>
            )}

            {/* Mobile menu toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg transition-colors"
              style={{ color: theme.text }}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 py-4 rounded-2xl" style={{ backgroundColor: theme.card }}>
            {navItems.map(item => (
              <a
                key={item.label}
                href={item.href}
                onClick={e => { e.preventDefault(); scrollToSection(item.href); }}
                className="block px-6 py-3 text-base font-medium transition-colors hover:opacity-70"
                style={{ color: theme.text }}
              >
                {item.label}
              </a>
            ))}
            <div className="px-6 pt-3 space-y-2">
              {user ? (
                <>
                  <Link
                    to="/portal"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="btn btn-gradient w-full text-sm"
                  >
                    My Portal
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="w-full text-sm font-medium py-2"
                    style={{ color: theme.textSecondary }}
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block w-full text-center text-sm font-medium py-2"
                    style={{ color: theme.textSecondary }}
                  >
                    Sign in
                  </Link>
                  <button
                    onClick={() => { setIsMobileMenuOpen(false); onDownloadClick(); }}
                    className="btn btn-gradient w-full text-sm"
                  >
                    Download App
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;